import type { QueryClient } from "@tanstack/react-query";
import { createRouter, Link, type RouterHistory } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";

import { Button } from "#/shared/components/button";
import { EmptyState } from "#/shared/components/empty-state";
import { Spinner } from "#/shared/components/spinner";
import { MdSymbol } from "#/shared/components/symbol";
import { makeQueryClient } from "#/shared/data/query";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { isOfflineError, Offline } from "./shared/components/empty-state/offline";

// Create a new router instance
export const getRouter = ({
  history,
  queryClient = makeQueryClient(),
}: {
  history?: RouterHistory;
  queryClient?: QueryClient;
} = {}) => {
  const router = createRouter({
    routeTree,
    history,
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultPreload: "intent",
    context: { queryClient },
    defaultViewTransition: true,
    defaultPendingComponent: () => <Spinner size={64} style={{ margin: "auto" }} />,
    defaultErrorComponent: ({ error, reset }) =>
      isOfflineError(error) ? (
        <Offline />
      ) : (
        <EmptyState
          icon={<MdSymbol>error</MdSymbol>}
          title="Error"
          subtitle={error.message || "An error occurred."}
        >
          <Button onClick={reset}>Retry</Button>
        </EmptyState>
      ),
    defaultNotFoundComponent: () => (
      <EmptyState
        icon={<MdSymbol>warning</MdSymbol>}
        title="Not found"
        subtitle={
          <>
            Try heading back to the <Link to="/">home page</Link>.
          </>
        }
      />
    ),
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
  });

  return router;
};
